import { useCallback, useEffect, useState } from 'react'
import type { ApiResult } from '../../lib/api'

export type Load<T> =
  | { state: 'loading' }
  | { state: 'ready'; data: T }
  | { state: 'error'; status: number }

/**
 * One API read, with its three outcomes kept separate.
 *
 * Loading is distinct from empty and empty is distinct from failed, which matters more here than
 * in most products: a security dashboard that renders a failed read as an empty one tells a
 * customer they have no findings when the truth is that nobody asked. Every caller of this has to
 * name all three branches, because the type gives it no way to fall through.
 */
/**
 * @param pollWhile
 * Asked of every successful answer. While it says true, the call is repeated -- so a page watching
 * a running scan keeps up with it instead of freezing at whatever was true when it mounted.
 *
 * A predicate rather than an interval, because the page cannot know whether polling is wanted
 * until it has seen the data: a finished scan must not be re-fetched for ever, and a running one
 * must not need a manual refresh to move. Returning false ends it; there is nothing to cancel.
 */
/**
 * @param keepPrevious
 * When true, a re-run triggered by a deps change does NOT blank back to `loading` — the last
 * ready (or error) state stays on screen until the new answer lands. For a view that re-fetches
 * itself after a write (the finding detail after a Save), dropping to a spinner would unmount the
 * whole body, lose an in-place confirmation, and flash. The FIRST load still shows loading, because
 * the initial state is loading and there is nothing to keep.
 */
export function useApi<T>(
  call: () => Promise<ApiResult<T>>,
  deps: unknown[],
  pollWhile?: (data: T) => boolean,
  keepPrevious = false,
): Load<T> {
  const [load, setLoad] = useState<Load<T>>({ state: 'loading' })

  // The caller passes a fresh closure every render, so the effect is keyed on the deps it
  // declares rather than on the function identity.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const run = useCallback(call, deps)

  useEffect(() => {
    // A slow first request followed by a fast second one must not let the first answer land last.
    let live = true
    let timer: ReturnType<typeof setTimeout> | undefined

    // Only before the FIRST request. A poll that reset this would blank the card it is updating
    // every few seconds, so a running scan would flicker between its progress and a spinner. And
    // when the caller opted to keep the previous answer, a deps-change re-run leaves what is on
    // screen in place until the new one lands, rather than dropping to a spinner.
    if (!keepPrevious) setLoad({ state: 'loading' })

    async function ask() {
      const result = await run()
      if (!live) return

      setLoad(result.ok
        ? { state: 'ready', data: result.data }
        : { state: 'error', status: result.status })

      // Only on success. A failed poll stops rather than hammering a server that is already
      // unhappy, and the error it leaves on screen says nothing here has been checked.
      if (result.ok && pollWhile?.(result.data)) {
        timer = setTimeout(ask, 4000)
      }
    }

    void ask()

    return () => {
      live = false
      if (timer) clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run])

  return load
}

/** What a failed read says, by status. Never the server's own message — see api.ts. */
export function describeFailure(status: number): string {
  if (status === 0) return 'Could not reach the server. Nothing on this page has been checked.'
  if (status === 401) return 'Your session has ended. Sign in again to see this tenant.'
  if (status === 403) return 'You do not have access to this workspace.'
  if (status === 404) return 'Not found.'

  // The reference route answers 409 when two findings share a prefix. Undifferentiated "could
  // not load" would send somebody looking for an outage instead of typing two more characters.
  if (status === 409) {
    return 'More than one finding starts with that reference. Open it from the findings list, ' +
      'or use more of the reference.'
  }
  return 'Could not load this. Nothing here has been checked — this is not an all-clear.'
}
